import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { PrincipalFormData, PrincipalFormErrors } from "../types";

interface CommissionInformationFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onAddCommissionSlab: () => void;
  onRemoveCommissionSlab: (id: string) => void;
  onCommissionSlabChange: (id: string, field: "range" | "percentage", value: string) => void;
}

const CommissionInformationForm: React.FC<CommissionInformationFormProps> = ({
  formData,
  formErrors,
  onChange,
  onAddCommissionSlab,
  onRemoveCommissionSlab,
  onCommissionSlabChange,
}) => {
  const isPercentage = formData.commissionType === "percentage" || formData.commissionType === "precentage";
  const isFixed = formData.commissionType === "fixed";

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <h2 className="text-lg font-semibold mb-4">
        Commission Information
      </h2>
      <div className="space-y-6">
        {/* Commission Type */}
        <div>
          <label htmlFor="commissionType" className="block text-sm font-medium text-gray-700 mb-2">
            Commission Type <span className="text-red-500">*</span>
          </label>
          <select
            id="commissionType"
            name="commissionType"
            value={formData.commissionType}
            onChange={onChange}
            className={`block w-full px-3 py-2 border ${formErrors.commissionType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand appearance-none`}
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed</option>
          </select>
          {formErrors.commissionType && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.commissionType}
            </div>
          )}
        </div>

        {/* Commission Percentage - Show only when percentage is selected */}
        {isPercentage && (
          <div>
            <label htmlFor="commissionPercentage" className="block text-sm font-medium text-gray-700 mb-2">
              Commission Percentage <span className="text-red-500">*</span>
            </label>
            <input
              id="commissionPercentage"
              type="number"
              name="commissionPercentage"
              value={formData.commissionPercentage}
              onChange={onChange}
              placeholder="0"
              min="0"
              max="100"
              step="0.01"
              className={`block w-full px-3 py-2 border ${formErrors.commissionPercentage ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.commissionPercentage && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.commissionPercentage}
              </div>
            )}
          </div>
        )}

        {/* Commission Fixed Amount - Show only when fixed is selected */}
        {isFixed && (
          <div>
            <label htmlFor="commissionFixedAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Commission Fixed Amount <span className="text-red-500">*</span>
            </label>
            <input
              id="commissionFixedAmount"
              type="number"
              name="commissionFixedAmount"
              value={formData.commissionFixedAmount}
              onChange={onChange}
              placeholder="0.00"
              min="0"
              step="0.01"
              className={`block w-full px-3 py-2 border ${formErrors.commissionFixedAmount ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.commissionFixedAmount && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.commissionFixedAmount}
              </div>
            )}
          </div>
        )}

        {/* Commission Slabs */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Commission Slabs (Optional)
            </label>
            <button
              type="button"
              onClick={onAddCommissionSlab}
              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Slab
            </button>
          </div>
          
          {formData.commissionSlabs.length > 0 && (
            <div className="space-y-3 border border-gray-300 rounded-lg p-4 bg-gray-50">
              {formData.commissionSlabs.map((slab) => (
                <div key={slab.id} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={slab.range}
                    onChange={(e) => onCommissionSlabChange(slab.id, "range", e.target.value)}
                    placeholder="e.g., 0-1000 or 5001+"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                  />
                  <input
                    type="number"
                    value={slab.percentage}
                    onChange={(e) => onCommissionSlabChange(slab.id, "percentage", e.target.value)}
                    placeholder="Percentage"
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveCommissionSlab(slab.id)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {formErrors.commissionSlabs && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.commissionSlabs}
            </div>
          )}
          
          <p className="text-sm text-gray-500 mt-2">
            Add commission slabs with range (e.g., "0-1000", "1001-5000", "5001+") and corresponding percentage
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommissionInformationForm;
